'use client';

import { useCallback, useEffect, useState } from 'react';
import { UPZI_LOGIN_SUCCESS_EVENT_NAME } from '@/common/constant';
import BenefitsTitleIcon from '@upzi/web-shell/icons/benefits-title-icon';
import {
  getSaveOpportunityTrackingProperties,
  useOpportunityTracking,
} from '@/hooks/use-opportunity-tracking';
import { cn } from '@/lib/utils';
import type { SimilarJob } from '@/services/jobs/model';
import { useSavedJobs } from '@/services/jobs/use-saved-jobs';
import { OPPORTUNITY_TRACKING_EVENTS } from '@/services/opportunity-tracking';
import { SOURCE_SCREENS } from '@/services/opportunity-tracking/source-screen';
import { SimilarJobCard } from './similar-job-card';

export function SimilarJobsSection({
  className,
  isLoggedIn = false,
  jobs,
}: {
  className?: string;
  isLoggedIn?: boolean;
  jobs: SimilarJob[];
}) {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const [hasLoggedInClient, setHasLoggedInClient] = useState(false);
  const isAuthenticated = isLoggedIn || hasLoggedInClient;
  const { checkSavedJobs, pendingJobId, savedJobIds, toggleSavedJob } =
    useSavedJobs({
      enabled: isAuthenticated,
      fetchOnMount: false,
      sourceScreen: SOURCE_SCREENS.OPPORTUNITY_DETAILS,
    });

  useEffect(() => {
    const handleLoginSuccess = () => setHasLoggedInClient(true);

    window.addEventListener(UPZI_LOGIN_SUCCESS_EVENT_NAME, handleLoginSuccess);

    return () =>
      window.removeEventListener(
        UPZI_LOGIN_SUCCESS_EVENT_NAME,
        handleLoginSuccess
      );
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    void checkSavedJobs(jobs.map(job => job.id));
  }, [checkSavedJobs, isAuthenticated, jobs]);

  const handleSaveChange = useCallback(
    (job: SimilarJob) => {
      void toggleSavedJob(job);
    },
    [toggleSavedJob]
  );

  const handleGuestSaveChange = useCallback(
    (job: SimilarJob) => {
      return trackOpportunityEvent({
        eventName: OPPORTUNITY_TRACKING_EVENTS.save,
        includeUserInfo: false,
        jobId: job.id,
        properties: getSaveOpportunityTrackingProperties(
          SOURCE_SCREENS.OPPORTUNITY_DETAILS
        ),
      });
    },
    [trackOpportunityEvent]
  );

  if (jobs.length === 0) return null;

  return (
    <section
      aria-labelledby="similar-jobs-title"
      className={cn('mt-6', className)}
    >
      <div className="mb-3 flex items-center gap-3">
        <h2
          id="similar-jobs-title"
          className="text-on-surface text-lg leading-5 font-semibold"
        >
          Việc làm bạn sẽ thích
        </h2>
        <BenefitsTitleIcon />
      </div>
      <div className="space-y-3">
        {jobs.map(job => (
          <SimilarJobCard
            key={job.id}
            isSavePending={pendingJobId === job.id}
            isSaved={savedJobIds.has(job.id)}
            job={job}
            onSaveChange={
              isAuthenticated ? handleSaveChange : handleGuestSaveChange
            }
            requiresLogin={!isAuthenticated}
          />
        ))}
      </div>
    </section>
  );
}
